/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

/**
 * Webview detection.
 *
 * The portal is embedded inside the native Grab Merchant app's webview.
 * The User-Agent is the source of truth: the native app appends a `Grab
 * Merchant` token to the webview UA (a production contract also relied on by
 * mex-mobile-web), which is present synchronously on every load and every
 * client-side route — so no caching is needed to survive navigation. Detection
 * keys on that name alone; the version and channel that follow it are parsed
 * separately, so a change there can never cost us the app context. The
 * `?webview=true` query param is only a manual override so app-context behaviour
 * can be exercised in a desktop browser, where the native UA can't be faked.
 *
 * This answers "are we inside the app", nothing more. It gates **app-context**
 * behaviour only — hiding the sidebar and features the app does not offer,
 * routing an action to a native JS callback when its destination exists only in
 * the app (hand-off is not hiding: the control still renders in both hosts, only
 * where it goes changes), and telling the app whether to draw its own bottom nav
 * bar around the webview (BP-55782). The mobile *design* is width-driven (Duxton
 * breakpoints), and the page header, title and back control render in every
 * context: the app draws no top nav bar, so the web header is the only one.
 * See `.claude/reference/ecommerce-mobile-webview.md`.
 */
export const WEBVIEW_QUERY_PARAM = "webview"
export const WEBVIEW_BODY_CLASS = "webview-mode"

// Name only: anchoring on "/<digit>" is what let "Grab Merchant GEA/4.146.0"
// (Grab Early Access) read as a plain browser. No browser UA carries the name,
// so matching it alone has no false-positive risk.
const GRAB_MERCHANT_UA = /Grab\s+Merchant/i

const readFromUserAgent = (): boolean => {
  try {
    return GRAB_MERCHANT_UA.test(window.navigator.userAgent)
  } catch {
    return false
  }
}

const readFromQuery = (): boolean => {
  try {
    return (
      new URLSearchParams(window.location.search).get(WEBVIEW_QUERY_PARAM) ===
      "true"
    )
  } catch {
    return false
  }
}

/**
 * Returns true when the portal is running inside the native app webview.
 * Resolved fresh from the UA (source of truth), or the `?webview=true` override
 * used for desktop testing.
 */
export const isWebview = (): boolean => {
  if (typeof window === "undefined") return false
  return readFromUserAgent() || readFromQuery()
}

/**
 * Structured contents of the `Grab Merchant/...` UA token, observed as
 * `Grab Merchant/4.146.0 (ios 18.2; Build 126852500)` on iOS and
 * `Grab Merchant/4.146.0 (android 13; Build 245)` on Android. Only the version
 * is required to parse; every other field degrades to "" if the app ever
 * reformats the parenthetical, so a format drift loses detail, never the
 * version.
 */
export interface MexAppInfo {
  /** Native app version, e.g. "4.146.0". */
  appVersion: string
  /**
   * Distribution channel marker carried between the app name and the version,
   * lowercased — "gea" on Grab Early Access builds, "" on production. Recorded
   * verbatim rather than matched against a known list, so a channel introduced
   * after this code ships is still visible instead of collapsing to "".
   */
  appChannel: string
  /** "ios" | "android", or "" when the token names neither. */
  platform: string
  /** Host OS version from the token, e.g. "18.2" / "13". */
  osVersion: string
  /** Native build number from the token, e.g. "126852500". */
  appBuild: string
}

// Anchored on "/<digit>" but not pinned to x.y.z, with an optional channel
// token before the delimiter. The token may hold anything but whitespace and
// "/": it must not cross a space, or an unrelated tail would parse as a channel
// and a version ("Grab Merchant Desktop AppleWebKit/537.36"), but it must allow
// punctuation, or a marker like "Early-Access" would fail the whole match and
// cost the version too — the one field this parser must never lose.
const GRAB_MERCHANT_UA_INFO = /Grab\s+Merchant(?:\s+([^\s/]+))?\/(\d[\w.-]*)(?:\s*\(([^)]*)\))?/i
const UA_PLATFORM = /\b(ios|android)\b\s*([\w.]*)/i
const UA_BUILD = /\bBuild\s+([\w.-]+)/i

/**
 * Parses the native app's UA token. Returns null outside the app webview (or
 * under the `?webview=true` desktop override, which has no native UA token).
 */
export const getMexAppInfo = (): MexAppInfo | null => {
  let userAgent = ""
  try {
    userAgent = window.navigator.userAgent
  } catch {
    return null
  }
  const match = GRAB_MERCHANT_UA_INFO.exec(userAgent)
  if (!match) return null

  // Groups: 1 = channel marker (absent on production), 2 = version, 3 = detail.
  const detail = match[3] ?? ""
  // Platform is read from the token's parenthetical first ("ios 18.2"), falling
  // back to the wider UA (iPhone/Android markers) if the parenthetical is
  // missing — same fallback mex-mobile-web uses. The OS version only exists in
  // the parenthetical.
  const detailPlatform = UA_PLATFORM.exec(detail)
  let platform = detailPlatform?.[1].toLowerCase() ?? ""
  if (!platform) {
    if (/iPhone|iPad|iPod/i.test(userAgent)) platform = "ios"
    else if (/Android/i.test(userAgent)) platform = "android"
  }

  return {
    appVersion: match[2],
    appChannel: match[1]?.toLowerCase() ?? "",
    platform,
    osVersion: detailPlatform?.[2] ?? "",
    appBuild: UA_BUILD.exec(detail)?.[1] ?? "",
  }
}

/**
 * App version in mex-mobile-web's `dvc` facet format — `mex-ios-4.146.0` /
 * `mex-adr-4.146.0` — so Scribe events here can be joined with mex-mobile-web
 * data on the same value. An unrecognized platform yields `mex-unknown-<ver>`
 * (mex-mobile-web returns null there, but dropping the version would cost the
 * one field this ticket exists to record). Null outside the app webview.
 */
const FACET_PLATFORM: Record<string, string> = { ios: "ios", android: "adr" }

export const getMexAppVersionFacet = (): string | null => {
  const info = getMexAppInfo()
  if (!info) return null
  const platform = FACET_PLATFORM[info.platform] ?? "unknown"
  return `mex-${platform}-${info.appVersion}`
}

/**
 * Adds the `webview-mode` class to <body> when running in the webview, so
 * global/layout CSS can switch to the mobile design. Safe to call at bootstrap.
 */
export const applyWebviewBodyClass = (): void => {
  if (typeof document === "undefined") return
  if (isWebview()) {
    document.body.classList.add(WEBVIEW_BODY_CLASS)
  }
}

/**
 * The `ecom-page` body class lets global CSS release the desktop content-width
 * floor so eCom pages are RESPONSIVE at any width (see `body.ecom-page` in
 * styles/global.ts). Unlike the webview class, this is driven by the ROUTE, not by
 * the app context — call it at bootstrap and on every client-side route change with
 * whether the current path is an eCom route (see `isEcomPath` in constants/routes).
 */
export const ECOM_PAGE_BODY_CLASS = "ecom-page"

export const applyEcomPageBodyClass = (isEcomPage: boolean): void => {
  if (typeof document === "undefined") return
  document.body.classList.toggle(ECOM_PAGE_BODY_CLASS, isEcomPage)
}

/**
 * The `ecom-full-bleed` body class releases the shell's centred content-width CAP
 * (`max-width`), letting a page's header band and sticky action bar run edge to
 * edge while the page keeps its own inner cap on the readable content. Route-driven
 * like `ecom-page`, and deliberately narrower than it — see `isEcomFullBleedPath` in
 * constants/routes.
 */
export const ECOM_FULL_BLEED_BODY_CLASS = "ecom-full-bleed"

export const applyEcomFullBleedBodyClass = (isFullBleed: boolean): void => {
  if (typeof document === "undefined") return
  document.body.classList.toggle(ECOM_FULL_BLEED_BODY_CLASS, isFullBleed)
}
